import React, { useEffect, useState } from 'react';
import { shiftsAPI, employeesAPI, companiesAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Loader2, Clock4, Edit, Trash2, UserPlus } from 'lucide-react';

const DAYS = [
  { value: 0, label: 'Mon' }, { value: 1, label: 'Tue' }, { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' }, { value: 4, label: 'Fri' }, { value: 5, label: 'Sat' }, { value: 6, label: 'Sun' },
];
const defaultForm = { name: '', start_time: '09:00', end_time: '18:00', break_minutes: 60, working_days: [0, 1, 2, 3, 4], company_id: '', notes: '', enabled: true };

const Shifts = () => {
  const [shifts, setShifts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [acting, setActing] = useState(false);
  const [assignDialog, setAssignDialog] = useState(null);
  const [selected, setSelected] = useState([]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, c, e] = await Promise.all([shiftsAPI.list(), companiesAPI.getAll(), employeesAPI.getAll()]);
      setShifts(s.data);
      setCompanies(c.data);
      setEmployees(e.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const openCreate = () => { setEditing(null); setForm(defaultForm); setCreateOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name, start_time: s.start_time, end_time: s.end_time,
      break_minutes: s.break_minutes || 60, working_days: s.working_days || [],
      company_id: s.company_id || '', notes: s.notes || '', enabled: s.enabled !== false,
    });
    setCreateOpen(true);
  };

  const toggleDay = (d) => {
    const wd = form.working_days.includes(d) ? form.working_days.filter(x => x !== d) : [...form.working_days, d].sort();
    setForm({ ...form, working_days: wd });
  };

  const save = async () => {
    if (!form.name || !form.start_time || !form.end_time) { toast.error('Name + times required'); return; }
    setActing(true);
    try {
      const payload = { ...form, break_minutes: parseInt(form.break_minutes || 0), company_id: form.company_id || null };
      if (editing) await shiftsAPI.update(editing.id, payload);
      else await shiftsAPI.create(payload);
      toast.success(editing ? 'Shift updated' : 'Shift created');
      setCreateOpen(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActing(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete shift? Employees will be unassigned.')) return;
    try { await shiftsAPI.delete(id); toast.success('Deleted'); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openAssign = (s) => { setAssignDialog(s); setSelected([]); };
  const doAssign = async () => {
    if (!selected.length) { toast.error('Select at least one employee'); return; }
    setActing(true);
    try {
      const r = await shiftsAPI.assign(assignDialog.id, selected);
      toast.success(r.data.message || 'Assigned');
      setAssignDialog(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActing(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="shifts-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Clock4 />Shift Management</h1>
          <p className="text-muted-foreground">Define work shifts and assign them to employees</p>
        </div>
        <Button onClick={openCreate} data-testid="add-shift-btn"><Plus size={16} className="mr-2" />New Shift</Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Total Shifts</p><p className="text-2xl font-bold">{shifts.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Enabled</p><p className="text-2xl font-bold text-emerald-600">{shifts.filter(s => s.enabled !== false).length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Total Assignments</p><p className="text-2xl font-bold">{shifts.reduce((sum, s) => sum + (s.assigned_count || 0), 0)}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Unassigned Employees</p><p className="text-2xl font-bold text-amber-600">{employees.filter(e => !e.shift_id && e.status === 'active').length}</p></CardContent></Card>
      </div>

      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Name</TableHead><TableHead>Hours</TableHead><TableHead>Days</TableHead>
            <TableHead>Break</TableHead><TableHead>Company</TableHead><TableHead>Assigned</TableHead>
            <TableHead>Status</TableHead><TableHead>Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {shifts.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">No shifts defined</TableCell></TableRow>}
            {shifts.map(s => (
              <TableRow key={s.id} data-testid={`shift-row-${s.id}`}>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell className="font-mono text-sm">{s.start_time} – {s.end_time}</TableCell>
                <TableCell>
                  <div className="flex gap-0.5">
                    {DAYS.map(d => (
                      <span key={d.value} className={`px-1.5 py-0.5 text-xs rounded ${s.working_days?.includes(d.value) ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>{d.label[0]}</span>
                    ))}
                  </div>
                </TableCell>
                <TableCell>{s.break_minutes || 0}m</TableCell>
                <TableCell className="text-xs">{companies.find(c => c.id === s.company_id)?.name || 'All'}</TableCell>
                <TableCell><Badge variant="outline">{s.assigned_count || 0}</Badge></TableCell>
                <TableCell><Badge variant={s.enabled !== false ? 'default' : 'outline'}>{s.enabled !== false ? 'Enabled' : 'Disabled'}</Badge></TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => openAssign(s)} data-testid={`assign-shift-${s.id}`} title="Assign employees"><UserPlus size={14} /></Button>
                    <Button size="sm" variant="outline" onClick={() => openEdit(s)} data-testid={`edit-shift-${s.id}`}><Edit size={14} /></Button>
                    <Button size="sm" variant="outline" onClick={() => remove(s.id)} data-testid={`del-shift-${s.id}`}><Trash2 size={14} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      {/* Create / Edit */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'New'} Shift</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Morning, Night, Flex..." data-testid="shift-name" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Start *</Label><Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="shift-start" /></div>
              <div><Label>End *</Label><Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="shift-end" /></div>
              <div><Label>Break (min)</Label><Input type="number" value={form.break_minutes} onChange={(e) => setForm({ ...form, break_minutes: e.target.value })} /></div>
            </div>
            <div>
              <Label>Working Days</Label>
              <div className="flex gap-2 mt-1 flex-wrap">
                {DAYS.map(d => (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => toggleDay(d.value)}
                    className={`px-3 py-1.5 text-sm rounded-md border transition ${form.working_days.includes(d.value) ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-muted-foreground'}`}
                    data-testid={`day-${d.value}`}
                  >{d.label}</button>
                ))}
              </div>
            </div>
            <div>
              <Label>Company (optional)</Label>
              <Select value={form.company_id || 'all'} onValueChange={(v) => setForm({ ...form, company_id: v === 'all' ? '' : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All companies</SelectItem>
                  {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <div className="flex items-center gap-2">
              <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
              <Label className="text-sm">Enabled</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={acting} data-testid="save-shift-btn">{acting && <Loader2 size={14} className="animate-spin mr-1" />}{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign employees */}
      <Dialog open={!!assignDialog} onOpenChange={(o) => !o && setAssignDialog(null)}>
        <DialogContent className="max-w-xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader><DialogTitle>Assign {assignDialog?.name} to employees</DialogTitle></DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-1 pr-2">
            {employees.filter(e => e.status === 'active').map(e => (
              <label key={e.id} className="flex items-center gap-3 p-2 hover:bg-muted/50 rounded cursor-pointer">
                <Checkbox
                  checked={selected.includes(e.id)}
                  onCheckedChange={(v) => setSelected(v ? [...selected, e.id] : selected.filter(x => x !== e.id))}
                  data-testid={`select-emp-${e.id}`}
                />
                <div className="flex-1">
                  <p className="text-sm font-medium">{e.first_name} {e.last_name}</p>
                  <p className="text-xs text-muted-foreground">{e.employee_code} · {e.designation || '—'}{e.shift_id && e.shift_id !== assignDialog?.id ? <span className="text-amber-600 ml-2">currently on another shift</span> : ''}</p>
                </div>
              </label>
            ))}
          </div>
          <DialogFooter>
            <span className="text-sm text-muted-foreground mr-auto">{selected.length} selected</span>
            <Button variant="outline" onClick={() => setAssignDialog(null)}>Cancel</Button>
            <Button onClick={doAssign} disabled={acting || !selected.length} data-testid="confirm-assign-shift-btn">{acting && <Loader2 size={14} className="animate-spin mr-1" />}Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Shifts;
